import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import { AgentRecord } from "../AgentRecord";

/**
 * Every agent in the org, in the order the server lists them (newest first).
 *
 * ``GET /v1/ai/agents`` answers 100 at a time and takes no limit, so this
 * follows ``next_token`` to the end before resolving: the Agents tab and the
 * agent pickers all filter and sort in the browser, and a page that only
 * held the first 100 would quietly leave agents out of those results.
 * Agents are a configured set, dozens per org, so the whole list is small.
 */
export function useAgents(options?: {
  orgId?: string;
}): UseQueryResult<AgentRecord[], Error> {
  const { agentService } = useDomainServices();
  const orgId = options?.orgId;

  return useQuery<AgentRecord[], Error>({
    queryKey: ["agents", orgId],
    queryFn: async ({ signal }) => {
      const collected: AgentRecord[] = [];
      let pageToken: string | undefined = undefined;
      do {
        const page = await agentService.listAgents({
          orgId,
          pageToken,
          abortSignal: signal,
        });
        collected.push(...page.items);
        pageToken = page.next_token ?? undefined;
      } while (pageToken !== undefined);
      return collected;
    },
    enabled: orgId !== undefined,
    staleTime: 30_000,
  });
}
